import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as ArrowIcon } from "assets/icons/arrowIcon.svg";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import { makeStyles } from "tss-react/mui";
import ColourConstants from "helpers/colourConstants";
import useInfiniteScroll from "hooks/useDropdownInfiniteScroll";
import TableStyle from "styles/application/TableStyle";
import AddDialogStyle from "styles/application/AddDialogStyle";

import EMICheckbox from "./EMICheckbox";
import {
	DROPDOWN_LEFT_OFFSET,
	DROPDOWN_RIGHT_OFFSET,
	DROPDOWN_TOP_OFFSET,
} from "helpers/constants";
import { SHOW_ALL_OPTION } from "./Filters/constants";
import ErrorMessageWithErrorIcon from "./ErrorMessageWithErrorIcon";
import { handleSort } from "helpers/utils";
import { showError } from "../../redux/common/actions";
import { useDispatch } from "react-redux";

// Custom hook for dropdown height calculations
const useDropdownHeight = (showSearchBox) => {
	const calculateDropdownHeight = useCallback((parentRect, viewportHeight) => {
		const spaceBelow = viewportHeight - parentRect.bottom;
		const spaceAbove = parentRect.top;
		const searchBoxHeight = showSearchBox ? 60 : 0;
		const paddingAndBorder = 20;
		const minHeight = 150;
		
		const openUpward = spaceBelow < 300 && spaceAbove > spaceBelow;
		
		const maxAvailableHeight = openUpward
			? Math.min(spaceAbove - 20, viewportHeight * 0.9)
			: Math.min(spaceBelow - 20, viewportHeight * 0.9);
			
		const finalHeight = Math.max(minHeight, maxAvailableHeight - searchBoxHeight - paddingAndBorder);
		
		return {
			openUpward,
			maxHeight: `${finalHeight}px`,
			spaceAbove,
			spaceBelow
		};
	}, [showSearchBox]);
	
	return { calculateDropdownHeight };
};

const ADD = AddDialogStyle();
const AT = TableStyle();

const media = "@media (max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	tableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderTopColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackground,
		fontWeight: "bold",
		borderRightColor: "#979797",
		borderRightStyle: "solid",
		borderRightWidth: "1px",
	},
	tableBody: {
		whiteSpace: "noWrap",
	},
	selectedTableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackgroundSelected,
		fontWeight: "bold",
		color: "#FFFFFF",
	},
	nameRow: {
		minWidth: "150px",
		width: "100%",
		// maxWidth: "250px",
		padding: "15px",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",

		[media]: {
			width: 130,
		},
		flexGrow: "1",
		cursor: "pointer",
	},
	header: {
		display: "flex",
		width: "100%",
	},
	clear: {
		cursor: "pointer",
		marginLeft: "auto",
	},
	droplistitem: {
		// maxWidth: "250px",
		minWidth: "150px",
		width: "100%",

		overflow: "hidden",
		wordBreak: "break-word",
		padding: "0 15px",
	},
	firstdroplistItem: {
		display: "flex",
		alignItems: "center",
	},
}));

function DynamicDropdown(props) {
	const { classes, cx } = useStyles();
	const {
		width,
		selectedValue,
		selectedValues,
		selectdValueToshow,
		dataSource,
		columns,
		dataHeader,
		showHeader,
		onChange,
		placeholder,
		label,
		required,
		disabled,
		page,
		onPageChange,
		handleSort,
		handleServerSideSort,
		isServerSide,
		showClear,
		onClear,
		handleServerSideSearch,
		icon,
		hasCheckBoxList,
		checklistChangeHandler,
		rolesChecklist,
		count,
		isReadOnly,
		showBorderColor,
		isError,
		fetchData,
		showErrorIcon = false,
		errorMessage = "",
		hasGroup,
		groupBy,
		PreloadedSearch,
		cacheDropDownData = true,
		dropDownActiveWidth = "500px",
		data,
		showSearchBox = true,
		multiSelect = false,
		maxDisplayItems = 2,
		useUniqueKey = false,
		highlight = false,
		highlightColor = "rgb(48, 122, 215)",
		highlightBackground = "#e7f1ff",
		autoSelectSingleOption = false,
	} = props;
	
	const { calculateDropdownHeight } = useDropdownHeight(showSearchBox);
	const [dropActive, setDropActive] = useState(false);
	const [filteredList, setFilteredList] = useState([]);
	const [originalFilteredList, setOriginalFilteredList] = useState([]);
	const [searchtext, setsearchText] = useState("");
	const [currentTableSort, setCurrentTableSort] = useState(["name", "asc"]);
	const [dropdownlistner, setdropdownlistner] = useState(window);
	const [isLoading, setLoading] = useState(false);
	const [mouseDown, setMouseDown] = useState(false);
	const [uniqueId] = useState(Math.random() * Date.now());
	const [badgeHovered, setBadgeHovered] = useState(false);
	const scrollRef = useRef(true);
	const focusRef = useRef(null);
	const optionsRef = useRef();

	const preloadSearch = useRef(false);
	useEffect(() => {
		setFilteredList(dataSource);
		setOriginalFilteredList(dataSource);

		if (isLoading) {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dataSource]);

	const dispatch = useDispatch();

	const handleOutsideClick = useCallback(
		(event) => {
			let specifiedElement = document.getElementsByClassName(
				"dropdown-expand active"
			)[0];
			let dropbox = document.getElementsByClassName("dropbox active")[0];
			let isClickInside =
				specifiedElement?.contains(event.target) ||
				dropbox?.contains(event.target);
			let parentEl = document.getElementsByClassName("dropdown active")[0];
			if (!isClickInside) {
				if (!cacheDropDownData) {
					setFilteredList([]);
					setOriginalFilteredList([]);
				}
				if (dropbox) dropbox.classList.remove("active");
				if (parentEl) parentEl.classList.remove("active");
				if (specifiedElement) {
					specifiedElement.classList.remove("active");

					specifiedElement.style.position = "fixed";
				}
				const dailogContent = document.getElementsByClassName(
					"MuiDialogContent-root"
				)[0];
				setDropActive(false);
				if (dailogContent) dailogContent.style.overflow = "auto";
			}
		},
		[cacheDropDownData]
	);


	useEffect(() => {
		window.addEventListener("click", handleOutsideClick);
		return () => {
			window.removeEventListener("click", handleOutsideClick);
		};
	}, [handleOutsideClick]);

	const { hasMore, loading, gotoTop, handleScroll } = useInfiniteScroll(
		dataSource,
		count,
		async (pageSize, prevData) => await onPageChange(pageSize + 1, prevData),
		page,
		searchtext,
		dropdownlistner
	);

	// scroll event of dropdown when mapped to DOM
	useEffect(() => {
		// Create stable function reference for proper cleanup
		const boundScrollHandler = (e) => handleScroll(e, uniqueId);
		let dropdownElement = null;

		if (dropActive && scrollRef.current === true) {
			dropdownElement = document.getElementById(uniqueId);
			if (dropdownElement) {
				dropdownElement.addEventListener("scroll", boundScrollHandler);
				setdropdownlistner(dropdownElement);
				scrollRef.current = false;
			}
		}

		return () => {
			// Use the same element reference if available, otherwise get by ID
			const elementToCleanup =
				dropdownElement || document.getElementById(uniqueId);
			if (elementToCleanup) {
				elementToCleanup.removeEventListener("scroll", boundScrollHandler);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dropActive, uniqueId]);

	useEffect(() => {
		// if search value passed from props filter searched value from dropdown
		if (PreloadedSearch && dropActive && preloadSearch.current === false && showSearchBox && focusRef.current) {
			focusRef.current.value = PreloadedSearch;
			onFilter(PreloadedSearch);
			preloadSearch.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [PreloadedSearch, dropActive, showSearchBox]);

	// search
	const onFilter = async (val) => {
		setsearchText(val);
		setLoading(true);
		if (isServerSide) {
			// server side search
			await handleServerSideSearch(val);
		} else {
			// client side search
			if (val !== "") {
				const searchedList = originalFilteredList.filter((item) =>
					columns
						.map((i) => i.name)
						.some((col) => {
							return item[col]?.match(new RegExp(val, "gi"));
						})
				);
				setFilteredList(searchedList);
			} else {
				setFilteredList(originalFilteredList);
			}

			setLoading(false);
		}
	};

	// Handlers
	const handleSortClick = (field) => {
		// Flipping current method
		const newMethod = currentTableSort[1] === "asc" ? "desc" : "asc";
		if (isServerSide) {
			handleServerSideSort(field, newMethod);
		} else {
			handleSort(filteredList, setFilteredList, field, newMethod);
		}

		// Updating header state
		setCurrentTableSort([field, newMethod]);
	};

	const handleDrpdwnClick = (target) => {
		if (isReadOnly) return;
		removeActiveDropdown();
		let el = target.closest(".dropbox");
		if (el) el.classList.add("active");
		const parentEl = target.closest(".dropdown");
		if (parentEl) parentEl.classList.add("active");
		const dropdownExpandEl = parentEl.querySelector(".dropdown-expand");
		if (dropdownExpandEl) dropdownExpandEl.classList.add("active");
		if (PreloadedSearch && showSearchBox && focusRef.current && focusRef.current.value === "") {
			onFilter("");
		}
		if (showSearchBox && focusRef.current) {
			focusRef.current.focus();
		}
		setDropActive(true);

		if (dropdownExpandEl) {
			dropdownExpandEl.style.position = "fixed";
			const dropdownPos = parentEl?.getBoundingClientRect();
			dropdownExpandEl.style.bottom = "unset";
			setTimeout(() => {
				const viewportHeight = window.innerHeight;
				const dropdownList = dropdownExpandEl.querySelector(".dynamic-drop-list");
				
				// Use hook to calculate dropdown positioning
				const { openUpward, maxHeight } = calculateDropdownHeight(dropdownPos, viewportHeight);
				
				if (openUpward) {
					// Position dropdown above the trigger
					dropdownExpandEl.style.top = `${dropdownPos.top - dropdownExpandEl.scrollHeight + 53}px`;
				} else {
					// Position dropdown below the trigger
					dropdownExpandEl.style.top = `${dropdownPos.top - DROPDOWN_TOP_OFFSET}px`;
				}
				
				// Apply calculated height
				if (dropdownList) {
					dropdownList.style.maxHeight = maxHeight;
				}

				if (
					parentEl.scrollWidth < dropdownExpandEl.scrollWidth &&
					window.innerWidth - el.getBoundingClientRect().right < 150
				) {
					const isScrollbarActive =
						document.body.scrollHeight > window.innerHeight;
					dropdownExpandEl.style.right = `${
						window.innerWidth -
						dropdownPos.right +
						(isScrollbarActive
							? DROPDOWN_RIGHT_OFFSET / 2
							: DROPDOWN_RIGHT_OFFSET)
					}px`;
				} else {
					dropdownExpandEl.style.left = `${
						dropdownPos.left + DROPDOWN_LEFT_OFFSET
					}px`;
				}
			}, 0);
		}
		if (scrollRef.current === true) {
			document
				.getElementById(uniqueId)
				.addEventListener("scroll", (e) => handleScroll(e, uniqueId));
			setdropdownlistner(document.getElementById(uniqueId));
			scrollRef.current = false;
		}
		// const dailogContent = document.getElementsByClassName(
		// 	"MuiDialogContent-root"
		// )[0];
		// if (dailogContent) dailogContent.style.overflow = "visible";
	};

	const removeActiveDropdown = () => {
		let specifiedElement = document.getElementsByClassName(
			"dropdown-expand active"
		)[0];
		let dropbox = document.getElementsByClassName("dropbox active")[0];

		let hoveredElement = document.querySelector(
			".dropdown-expand.active  .hoverOver"
		);
		if (hoveredElement) {
			hoveredElement.classList.remove("hoverOver");
		}

		if (dropbox) dropbox.classList.remove("active");
		let parentEl = document.getElementsByClassName("dropdown active")[0];
		if (parentEl) parentEl.classList.remove("active");
		if (specifiedElement) specifiedElement.classList.remove("active");
		setDropActive(false);
	};

	// clear and reset dropdown content
	const handleClear = (e) => {
		if (isReadOnly) return;
		setCurrentTableSort(["name", "asc"]);

		if (multiSelect) {
			onChange([]);
		} else {
			onChange({});
		}

		setsearchText("");
		if (showSearchBox && focusRef.current) {
			focusRef.current.value = "";
		}
		setFilteredList(originalFilteredList);
		onClear();
		e.stopPropagation();
	};

	const isValueSelected = () => {
		if (multiSelect) {
			return selectedValues && selectedValues.length > 0;
		}
		if (typeof selectedValue === "object") {
			return Boolean(selectedValue.name);
		}
		return Boolean(selectedValue);
	};

	// Helper functions for multi-select
	const isShowAllSelected = () => {
		if (!multiSelect) return false;
		return Array.isArray(selectedValues) && selectedValues.length === 1 &&
			   (selectedValues[0].id === '' || selectedValues[0].id === 'all');
	};

	const isItemSelected = (item) => {
		if (!multiSelect) {
			// Use uniqueKey for comparison when available to handle cases where name is empty but reference is unique
			if (useUniqueKey && item?.uniqueKey && selectedValue?.uniqueKey) {
				return item.uniqueKey === selectedValue.uniqueKey;
			}
			return item?.id === selectedValue?.id;
		}
		return Array.isArray(selectedValues) && selectedValues.some(selected => {
			if (useUniqueKey && item?.uniqueKey && selected?.uniqueKey) {
				return item.uniqueKey === selected.uniqueKey;
			}
			return selected.id === item.id;
		});
	};

	const getDisplayText = () => {
		if (multiSelect) {
			// Ensure selectedValues is an array
			const valuesArray = Array.isArray(selectedValues) ? selectedValues : [];

			if (valuesArray.length === 0) {
				return placeholder;
			}

			// Show All case
			if (isShowAllSelected()) {
				return "Show All";
			}

			// Multiple selections
			if (valuesArray.length <= maxDisplayItems) {
				return valuesArray.map(item => item.name).join(", ");
			}

			return `${valuesArray.slice(0, maxDisplayItems).map(item => item.name).join(", ")} +${valuesArray.length - maxDisplayItems} more`;
		}

		// Original single-select logic
		return selectedValue && selectedValue[selectdValueToshow] ?
			selectedValue[selectdValueToshow] : placeholder;
	};

	const handleMultiSelectClick = (item) => {
		if (!selectedValues) {
			// First selection
			onChange([item]);
			return;
		}

		const isShowAll = item.id === '' || item.id === 'all';
		const currentlySelected = selectedValues.some(selected => selected.id === item.id);

		if (isShowAll) {
			// Show All clicked - unselect everything else and select only Show All
			onChange([item]);
		} else {
			// Regular item clicked
			if (currentlySelected) {
				// Deselect item
				const newSelection = selectedValues.filter(selected => selected.id !== item.id);
				if (newSelection.length === 0) {
					// If nothing would be selected, default to Show All
					const showAllOption = filteredList.find(opt => opt.id === '' || opt.id === 'all') ||
										 originalFilteredList.find(opt => opt.id === '' || opt.id === 'all') ||
										 dataSource.find(opt => opt.id === '' || opt.id === 'all') ||
										 SHOW_ALL_OPTION;
					onChange([showAllOption]);
				} else {
					onChange(newSelection);
				}
			} else {
				// Select item - remove Show All if present and add new item
				const newSelection = selectedValues
					.filter(selected => selected.id !== '' && selected.id !== 'all')
					.concat([item]);
				onChange(newSelection);
			}
		}
	};

	const handleResetToShowAll = (e) => {
		e.stopPropagation();
		setBadgeHovered(false);

		// Find the Show All option from the data source
		const showAllOption = filteredList.find(item => item.id === '' || item.id === 'all') ||
							originalFilteredList.find(item => item.id === '' || item.id === 'all') ||
							dataSource.find(item => item.id === '' || item.id === 'all');

		if (showAllOption) {
			onChange([showAllOption]);
		} else {
			// Fallback: create a basic Show All option
			onChange([SHOW_ALL_OPTION]);
		}
	};

	const handleApiCall = async () => {
		if (isReadOnly || originalFilteredList?.length !== 0) return;

		setLoading(true);
		try {
			const response = await fetchData();
			if (PreloadedSearch) {
				setFilteredList((prev) =>
					[
						...(Array.isArray(response?.data) ? response?.data ?? [] : []),
						...(dataSource ?? []),
					]
						.filter((x) => Boolean(x))
						.reduce((acc, current) => {
							const x = acc.find((item) => item.id === current.id);
							if (!x) {
								return acc.concat([current]);
							} else {
								return acc;
							}
						}, [])
				);
				setOriginalFilteredList((prev) =>
					[
						...(Array.isArray(response?.data) ? response?.data ?? [] : []),
						...(dataSource || []),
					]
						.filter((x) => Boolean(x))
						.reduce((acc, current) => {
							const x = acc.find((item) => item.id === current.id);
							if (!x) {
								return acc.concat([current]);
							} else {
								return acc;
							}
						}, [])
				);
			} else {
				setFilteredList((prev) =>
					[
						...prev,
						...(Array.isArray(response?.data) ? response?.data ?? [] : []),
						...(dataSource ?? []),
					]
						.filter((x) => Boolean(x))
						.reduce((acc, current) => {
							const x = acc.find((item) => item.id === current.id);
							if (!x) {
								return acc.concat([current]);
							} else {
								return acc;
							}
						}, [])
				);
				setOriginalFilteredList((prev) =>
					[
						...prev,
						...(Array.isArray(response?.data) ? response?.data ?? [] : []),
						...(dataSource || []),
					]
						.filter((x) => Boolean(x))
						.reduce((acc, current) => {
							const x = acc.find((item) => item.id === current.id);
							if (!x) {
								return acc.concat([current]);
							} else {
								return acc;
							}
						}, [])
				);
			}
		} catch (error) {
			dispatch(showError("Failed to load data."));
		} finally {
			setLoading(false);
		}
	};

	const handleMouseDown = (event) => {
		setMouseDown(true);
	};
	useEffect(() => {
		let active = null;

		// Get the Selected Dropdown Item Element, if any, when dropdown opens
		if (dropActive && !isLoading) {
			active = document.querySelector(".dropdown-expand.active   .selected");
			// Wait for the dropdown to lay out before scrolling, otherwise the
			// list's max-height isn't applied yet and scrollIntoView is a no-op.
			if (active) {
				requestAnimationFrame(() => {
					if (typeof active?.scrollIntoView === "function") {
						active.scrollIntoView({ block: "center", behavior: "auto" });
					}
				});
			}
			document.addEventListener("keydown", onKeyPress);
		}

		function onKeyPress(event) {
			// Store the selected dropdown item or hovering dropdown item on every keypress. Required for changing the hovering item when pressing up or down.
			const onHoverPresent =
				active ??
				document.querySelector(".dropdown-expand.active   .hoverOver");

			// If no selected item or hovering item present then set hover state to the first element
			if (!onHoverPresent) {
				const firstChildElement = document.querySelector(
					".dropdown-expand.active  .dynamic-drop-list"
				)?.firstChild;
				active = firstChildElement;
				active?.classList && active.classList.add("hoverOver");
			}

			// If hover state present then change hover state on up and down key.
			if (active && onHoverPresent) {
				active.classList.remove("hoverOver");
				if (event.keyCode === 40) {
					if (active.classList.contains("group-item")) {
						if (!active.nextElementSibling) {
							active = active.parentElement.nextElementSibling
								? active.parentElement.nextElementSibling.firstChild
								: active;
						} else {
							active = active.nextElementSibling;
						}
					} else {
						active = active.nextElementSibling || active;
					}
					active.scrollIntoView({
						behavior: "smooth",
						block: "end",
					});
				} else if (event.keyCode === 38) {
					if (active.classList.contains("group-item")) {
						if (!active.previousElementSibling) {
							active = active.parentElement.previousElementSibling
								? active.parentElement.previousElementSibling.lastChild
								: active;
						} else {
							active = active.previousElementSibling;
						}
					} else {
						active = active.previousElementSibling || active;
					}
					active.scrollIntoView({
						behavior: "smooth",
						block: "end",
					});
				} else if (event.keyCode === 13) {
					if (
						active.classList.contains("list-item") ||
						active.classList.contains("group-item")
					) {
						active.click();
					} else if (active.classList.contains("checklist-item")) {
						const checkboxItem = active.querySelector("input[type=checkbox]");
						if (checkboxItem) {
							checkboxItem.click();
						}
					}
					return;
				}

				active?.classList && active.classList.add("hoverOver");
			}
			setMouseDown(false);
		}

		const onTabPress = (e) => {
			setMouseDown(false);
		};

		document.addEventListener("keydown", onTabPress);
		document.addEventListener("mousedown", handleMouseDown);

		return () => {
			document.removeEventListener("mousedown", handleMouseDown);
			document.removeEventListener("keydown", onKeyPress);
			document.removeEventListener("keydown", onTabPress);
		};
	}, [dropActive, isLoading]);

	useEffect(() => {
		if (!autoSelectSingleOption || multiSelect || isReadOnly || selectedValue?.id) return;
		if (originalFilteredList.length === 1) {
			onChange(originalFilteredList[0]);
		}
		// onChange intentionally omitted: including it would require every caller to
		// wrap their handler in useCallback. The effect only fires when the list or
		// selection changes, so a stale onChange reference is not a practical risk.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [originalFilteredList, isReadOnly, selectedValue?.id]);

	return (
		<div
			className={`dropdown ${disabled ? "disabled" : ""} ${isReadOnly ? "readonly" : ""}`}
			style={{
				width: width,
				opacity: disabled && !hasCheckBoxList ? "0.4" : "1",
				cursor: disabled ? "not-allowed" : isReadOnly ? "not-allowed" : "pointer"
			}}
		>
			<div
				className={`dropbox ${dropActive ? "active" : ""}`}
				style={{ 
					width: "100%", 
					pointerEvents: (disabled || isReadOnly) ? "none" : "auto",
					cursor: isReadOnly ? "not-allowed" : "pointer"
				}}
				onClick={async (event) => {
					if (disabled || isReadOnly) {
						event.preventDefault();
						event.stopPropagation();
						return;
					}
					const target = event.target;
					handleDrpdwnClick(target);
					await handleApiCall();
					handleDrpdwnClick(target);
				}}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						fontFamily: "Roboto Condensed",
						fontWeight: "bold",
						fontSize: "14px",
					}}
				>
					<div className="caption-label">
						<Typography
							className="label"
							style={{
								display: "flex",
								alignItems: "center",
								columnGap: "10px",
							}}
						>
							<span>
								{label}
								{required && <span className="required">*</span>}
							</span>
							{showErrorIcon && (
								<ErrorMessageWithErrorIcon message={errorMessage} />
							)}
						</Typography>
					</div>

					{showClear && !isReadOnly && !(autoSelectSingleOption && originalFilteredList.length === 1) && (
						<Typography
							className={cx("label", classes.clear)}
							onClick={handleClear}
							style={{ color: "#E31212", textAlign: "right" }}
						>
							Clear
						</Typography>
					)}
				</div>

				<div
					tabIndex={0}
					onFocus={async (event) => {
						if (!mouseDown) {
							const target = event.target;
							handleDrpdwnClick(target);
							await handleApiCall();
							handleDrpdwnClick(target);
						}
					}}
					className={cx({
						"inputbox flex justify-between": true,
						"icon-box": icon,
						error: isError,
					})}
					style={{
						border:
							highlight
								? `2px solid ${highlightColor}`
								: showBorderColor && isValueSelected()
								? "2px solid rgb(48, 122, 215)"
								: isReadOnly
								? "0.9px solid #d1d5db"
								: "0.9px solid #b9b9b9",
						cursor: isReadOnly ? "not-allowed" : "pointer",
						backgroundColor: highlight
							? highlightBackground
							: isReadOnly
							? "#fafafa"
							: "transparent",
						minHeight: "52px",
						maxHeight: "52px",
						height: "52px",
						overflow: "hidden",
						alignItems: "center",
					}}
				>
					<span className="flex" style={{
						gap: "10px",
						flex: 1,
						overflow: "hidden",
						alignItems: "center",
						minWidth: 0
					}}>
						{icon && icon}
						{hasCheckBoxList ? (
							selectedValue ? (
								selectedValue
							) : (
								<em style={{ opacity: isReadOnly ? "0" : "0.7" }}>{placeholder}</em>
							)
						) : multiSelect ? (
							<span style={{
								color: isReadOnly ? "#4b5563" : "inherit",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								flex: 1,
								minWidth: 0
							}}>
								{getDisplayText()}
							</span>
						) : selectedValue && selectedValue[selectdValueToshow] ? (
							<span style={{
								color: isReadOnly ? "#4b5563" : "inherit",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								flex: 1,
								minWidth: 0
							}}>
								{selectedValue[selectdValueToshow]}
							</span>
						) : (
							<em style={{
								opacity: isReadOnly ? "0" : (placeholder !== "none" ? "0.7" : "0"),
								color: isReadOnly ? "#4b5563" : "inherit",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								flex: 1,
								minWidth: 0
							}}>
								{placeholder}
							</em>
						)}
					</span>

					{multiSelect && selectedValues && selectedValues.length > 0 && !isShowAllSelected() && (
						<span
							style={{
								backgroundColor: badgeHovered ? "#000000" : "rgb(48, 122, 215)",
								color: "#FFFFFF",
								borderRadius: "50%",
								padding: "0",
								fontSize: "12px",
								fontWeight: "bold",
								marginLeft: "8px",
								minWidth: "24px",
								width: "24px",
								maxHeight: "24px",
								height: "24px",
								textAlign: "center",
								cursor: "pointer",
								transition: "background-color 0.2s ease",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "2px",
								flexShrink: 0
							}}
							onMouseEnter={() => setBadgeHovered(true)}
							onMouseLeave={() => setBadgeHovered(false)}
							onClick={handleResetToShowAll}
							title="Click to reset to Show All"
						>
							{badgeHovered ? (
								<CloseIcon style={{ fontSize: "14px" }} />
							) : (
								selectedValues.length
							)}
						</span>
					)}

					{!isReadOnly && (
						<ArrowIcon
							className="arrow-down"
							style={{ flexShrink: 0 }}
						/>
					)}
				</div>
			</div>
			{/* {dropActive && ( */}
			<div
				className={cx({
					"dropdown-expand": true,
					// active: dropActive,
				})}
				onBlur={() => {
					if (!mouseDown) {
						removeActiveDropdown();
					}
					setDropActive(false);
					return;
				}}
				style={{
					width: dropDownActiveWidth,
					// Grow the panel so every column (each ~150px) fits; otherwise extra
					// columns (e.g. Arrangement) overflow outside the fixed panel width.
					minWidth: `${
						Math.max(columns?.length || 1, dataHeader?.length || 1) * 150 + 40
					}px`,
				}}
				tabIndex={"0"}
				role="button"
			>
				{showSearchBox && (
					<div className="search-box flex justify-between">
						<div className="input-field flex">
							<SearchIcon style={{ width: "20px" }} />
							<input
								type="text"
								className="search-box__text"
								placeholder="Search"
								onChange={(e) => {
									e.stopPropagation();
									onFilter(e.target.value);
								}}
								id="dynamic-dropdown-search-input"
								ref={focusRef}
								autoComplete="off"
							/>
						</div>

						<ArrowIcon className="arrow-down" />
					</div>
				)}
				<div className="dropdown-content">
					{showHeader && (
						<div className={classes.header}>
							{dataHeader.map((header, i) => (
								<div
									key={header.id}
									onClick={() => {
										handleSortClick(columns[i].name);
									}}
									className={cx(classes.nameRow, classes.tableHeadRow, {
										[classes.selectedTableHeadRow]:
											currentTableSort[0] === columns[i].name,
										[classes.tableHeadRow]:
											currentTableSort[0] !== columns[i].name,
									})}
									style={{
										minWidth: header.minWidth || "150px",
										width: header.width || "",
									}}
								>
									<span>{header.name}</span>
									<div className="arrow">
										<AT.DescArrow
											fill={
												currentTableSort[0] === columns[i].name &&
												currentTableSort[1] === "asc"
													? "#D2D2D9"
													: "#F9F9FC"
											}
											className="arrowUp"
										/>
										<AT.DefaultArrow
											fill={
												currentTableSort[0] === columns[i].name &&
												currentTableSort[1] === "desc"
													? "#D2D2D9"
													: "#F9F9FC"
											}
											className="arrowDown"
										/>
									</div>
								</div>
							))}
						</div>
					)}
					<div className="dynamic-drop-list" id={uniqueId}>
						{isLoading ? null : [...(data ? data : []), ...filteredList]
								?.length > 0 ? (
							<>
								{hasCheckBoxList
									? filteredList?.map((list) => (
											<div className="checklist-item flex" key={list?.id}>
												{columns.map((col) => (
													<ADD.CheckboxLabel key={col?.id}>
														<EMICheckbox
															state={
																rolesChecklist.filter(
																	(r) =>
																		r.id === list.id ||
																		r.id === list.modelVersionID
																).length === 1
															}
															changeHandler={() => {
																checklistChangeHandler(list.id, list[col.name]);
																// removeActiveDropdown();
																// setDropActive(false);
															}}
															disabled={disabled}
															tabIndex="-1"
														/>
														{list[col.name]}
													</ADD.CheckboxLabel>
												))}
											</div>
									  ))
									: multiSelect
									? filteredList?.map((list) => (
											<div className="checklist-item flex" key={list?.id}>
												{columns.map((col) => (
													<ADD.CheckboxLabel
														key={col?.id}
														onClick={() => {
															if (!disabled) {
																handleMultiSelectClick(list);
															}
														}}
														style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
													>
														<EMICheckbox
															state={isItemSelected(list)}
															changeHandler={() => {
																handleMultiSelectClick(list);
															}}
															disabled={disabled}
															tabIndex="-1"
														/>
														{list[col.name]}
													</ADD.CheckboxLabel>
												))}
											</div>
									  ))
									: hasGroup
									? groupBy.map((group) => {
											return (
												<div key={group.id} style={{}}>
													<div
														style={{
															padding: "15px 10px 15px 5px",
															backgroundColor: "#D8D8D8",
															cursor: "pointer",
															borderBottom: `1px solid ${ColourConstants.dropdownheaderBorder}`,
														}}
														onClick={(e) => {
															e.stopPropagation();
															if (!multiSelect) {
																onChange(group);
																setDropActive(false);
																removeActiveDropdown();
															}
															// Multi-select handled by checkbox changeHandler
														}}
														className={
															"group-item" +
															" " +
															(isItemSelected(group) ? "selected" : "")
														}
													>
														{multiSelect ? (
															<ADD.CheckboxLabel
																onClick={() => {
																	if (!disabled) {
																		handleMultiSelectClick(group);
																	}
																}}
																style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
															>
																<EMICheckbox
																	state={isItemSelected(group)}
																	changeHandler={() => {
																		handleMultiSelectClick(group);
																	}}
																	disabled={disabled}
																	tabIndex="-1"
																/>
																{group.name}
															</ADD.CheckboxLabel>
														) : (
															<span
																style={{
																	marginLeft: 5,
																	fontWeight: "500",
																	display: "flex",
																	alignItems: "center",
																	gap: "5px",
																	color:
																		isItemSelected(group)
																			? "rgb(35, 187, 121)"
																			: "",
																}}
															>
																{isItemSelected(group) && (
																	<CheckIcon className="check mr-sm" />
																)}
																{group.name}
															</span>
														)}
													</div>
													{filteredList
														?.filter((x) => x.groupBy === group.name)
														?.map((list) =>
															multiSelect ? (
																<div className="checklist-item flex" key={list.id} style={{ paddingLeft: "10px" }}>
																	{columns.map((col) => (
																		<ADD.CheckboxLabel
																			key={col?.id}
																			onClick={() => {
																				if (!disabled) {
																					handleMultiSelectClick(list);
																				}
																			}}
																			style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
																		>
																			<EMICheckbox
																				state={isItemSelected(list)}
																				changeHandler={() => {
																					handleMultiSelectClick(list);
																				}}
																				disabled={disabled}
																				tabIndex="-1"
																			/>
																			{list[col.name]}
																		</ADD.CheckboxLabel>
																	))}
																</div>
															) : (
																<div
																	className={
																		"list-item flex group-item " +
																		(isItemSelected(list)
																			? "selected"
																			: "")
																	}
																	style={{ paddingLeft: "10px" }}
																	key={list.id}
																	onClick={(e) => {
																		e.stopPropagation();
																		onChange(list);
																		setDropActive(false);
																		removeActiveDropdown();
																	}}
																>
																	{columns.map((col, i) => (
																		<span
																			style={{
																				flexGrow: "1",
																				minWidth: col.minWidth || "150px",
																			}}
																			className={cx(classes.droplistitem, {
																				[classes.firstdroplistItem]: i === 0,
																			})}
																			key={i}
																		>
																			{i === 0 && isItemSelected(list) && (
																				<CheckIcon className="check mr-sm" />
																			)}
																			{list[col.name]}
																		</span>
																	))}
																</div>
															)
														)}
												</div>
											);
									  })
									: [...(data ? data : []), ...filteredList]?.map(
											(list, index) => (
												<div
													ref={optionsRef}
													className={
														"list-item flex " +
														(isItemSelected(list) ? "selected" : "")
													}
													key={list.id}
													onClick={(e) => {
														e.stopPropagation();
														if (multiSelect) {
															handleMultiSelectClick(list);
														} else {
															onChange(list);
															setDropActive(false);
															removeActiveDropdown();
														}
													}}
													id={`list-${list.id}`}
												>
													{columns.map((col, i) => (
														<span
															style={{
																flexGrow: "1",
																minWidth: col.minWidth || "150px",
															}}
															className={cx(classes.droplistitem, {
																[classes.firstdroplistItem]: i === 0,
															})}
															key={i}
														>
															{i === 0 && isItemSelected(list) && <CheckIcon className="check mr-sm" />}
															{list[col.name]}
														</span>
													))}
												</div>
											)
									  )}
							</>
						) : !isLoading ? (
							<span className="no-record">No records found</span>
						) : (
							""
						)}
						{/* scroll to load */}
						{loading || isLoading ? (
							<div style={{ padding: "16px 10px" }}>
								<b>Loading...</b>
							</div>
						) : (
							""
						)}

						{!hasMore && (
							<div
								style={{ textAlign: "center", padding: "16px 10px" }}
								className="flex justify-center"
							>
								<span
									className="link-color ml-md cursor-pointer"
									onClick={() => gotoTop()}
								>
									Go to top
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
			{/* )} */}
		</div>
	);
}

export default DynamicDropdown;
DynamicDropdown.defaultProps = {
	width: "300px",
	dataSource: [],
	selectedValue: {},
	selectedValues: [],
	onChange: () => {},
	disabled: false,
	placeholder: "Select Item",
	label: "",
	dataHeader: [],
	page: 10,
	columns: [],
	required: false,
	showHeader: false,
	showClear: false,
	isServerSide: false,
	selectdValueToshow: "",
	isReadOnly: false,
	onClear: () => {},
	handleServerSideSearch: () => {},
	handleServerSideSort: () => {},
	onPageChange: () => {},
	fetchData: () => {},
	showBorderColor: false,
	handleSort: handleSort,
	showSearchBox: true,
	multiSelect: false,
	maxDisplayItems: 2,
	useUniqueKey: false,
	highlight: false,
	highlightColor: "rgb(48, 122, 215)",
	highlightBackground: "#e7f1ff",
	autoSelectSingleOption: false,
};

DynamicDropdown.propTypes = {
	width: PropTypes.string,
	dataSource: PropTypes.array,
	selectedValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	selectedValues: PropTypes.array,
	onChange: PropTypes.func,
	disabled: PropTypes.bool,
	placeholder: PropTypes.string,
	hasLabel: PropTypes.bool,
	label: PropTypes.string,
	page: PropTypes.number,
	dataHeader: PropTypes.array,
	required: PropTypes.bool,
	showHeader: PropTypes.bool,
	columns: PropTypes.array,
	isReadOnly: PropTypes.bool,
	showClear: PropTypes.bool,
	icon: PropTypes.node,
	isServerSide: PropTypes.bool,
	selectdValueToshow: PropTypes.string,
	onClear: PropTypes.func,
	handleServerSideSearch: PropTypes.func,
	handleServerSideSort: PropTypes.func,
	onPageChange: PropTypes.func,
	showBorderColor: PropTypes.bool,
	fetchData: PropTypes.func,
	showSearchBox: PropTypes.bool,
	multiSelect: PropTypes.bool,
	maxDisplayItems: PropTypes.number,
	useUniqueKey: PropTypes.bool,
	highlight: PropTypes.bool,
	highlightColor: PropTypes.string,
	highlightBackground: PropTypes.string,
	autoSelectSingleOption: PropTypes.bool,
};
